import { type ReactNode } from 'react'

import { IconChip } from '#app/components/ui/icon-chip.tsx'
import { type IconName } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'

/**
 * EmptyState: the one treatment for a list, table or listing with nothing in
 * it. Centred, an optional icon in a soft `lg` IconChip, an `h2` title, an
 * optional muted description and an optional action (a create button, a link
 * back to the index) below the text. The title is a heading so a screen
 * reader lands on the empty state in the page outline.
 */
function EmptyState({
	icon,
	title,
	description,
	action,
	className,
}: {
	icon?: IconName
	title: ReactNode
	description?: ReactNode
	action?: ReactNode
	className?: string
}) {
	return (
		<div
			data-slot="empty-state"
			className={cn(
				'flex flex-col items-center gap-3 py-16 text-center',
				className,
			)}
		>
			{icon ? (
				<IconChip icon={icon} size="lg" data-slot="empty-state-icon" />
			) : null}
			<h2 className="text-h6">{title}</h2>
			{description ? (
				<p className="text-body-sm text-muted-foreground max-w-prose">
					{description}
				</p>
			) : null}
			{action ? <div data-slot="empty-state-action">{action}</div> : null}
		</div>
	)
}

export { EmptyState }
